// frontend/src/components/ClientList.js
// Componente para mostrar una tabla de clientes con funcionalidades de filtro, añadir, editar y eliminar.

import React, { useEffect, useState } from 'react';
import { useClientStore } from '../store/clientStore';
import { useAuthStore } from '../store/authStore';
import LoadingSpinner from './LoadingSpinner';
import CustomConfirmDialog from './CustomConfirmDialog'; // Componente de confirmación personalizada
import { PencilIcon, TrashIcon, PlusCircleIcon, FunnelIcon } from '@heroicons/react/24/solid';

/**
 * @component ClientList
 * @description Muestra una tabla paginada de clientes, con opciones de filtrado, adición, edición y eliminación.
 * @param {object} props - Propiedades del componente.
 * @param {function} props.onAddClient - Callback para iniciar el modo de añadir cliente.
 * @param {function(object)} props.onEditClient - Callback para iniciar el modo de edición de un cliente.
 */
function ClientList({ onAddClient, onEditClient }) {
  const { clients, fetchClients, deleteClient, isLoadingClients,
          opportunityNumbers, accountManagers, solutionSales } = useClientStore();
  const token = useAuthStore((state) => state.token);
  // Estado para filtros
  const [filters, setFilters] = useState({
    opportunityNumber: '',
    accountManager: '',
    solutionSales: '',
  });

  // Estado para la confirmación de eliminación
  const [showConfirm, setShowConfirm] = useState(false);
  const [clientIdToDelete, setClientIdToDelete] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);

  // Cargar clientes al montar el componente o cuando cambian los filtros/token
  useEffect(() => {
    fetchClients(filters);
  }, [fetchClients, token, filters]);

  // Manejador de cambios en los filtros
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  // Función para confirmar la eliminación
  const confirmDelete = (clientId) => {
    setClientIdToDelete(clientId);
    setShowConfirm(true);
  };

  // Función para ejecutar la eliminación después de la confirmación
  const handleDeleteConfirmed = async () => {
    if (clientIdToDelete) {
      await deleteClient(clientIdToDelete);
      setClientIdToDelete(null);
      setSelectedClientId(null);
    }
    setShowConfirm(false);
  };

  // Función para cancelar la eliminación
  const handleDeleteCancelled = () => {
    setClientIdToDelete(null);
    setShowConfirm(false);
  };

  const toggleSelectedClient = (clientId) => {
    setSelectedClientId((currentClientId) => currentClientId === clientId ? null : clientId);
  };

  // Fallback para iconos si heroicons/react no funciona en el entorno
  const DefaultPlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
  const DefaultPencilIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
    </svg>
  );
  const DefaultTrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.927a2.25 2.25 0 01-2.244-2.077L4.74 6.786m1.16-4.636A.75.75 0 005.135 2.25H2.25c-.414 0-.75.336-.75.75s.336.75.75.75H4.5l.343 12.926c0 .185.012.368.035.55l.008.13a6.49 6.49 0 00.183 1.838c.175.405.41.776.699 1.104C7.79 21.05 8.79 21.75 9.79 22.218m-.548-.548L12 18.293l2.75 2.75" />
    </svg>
  );
  const DefaultFunnelIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M6 12H3m18-6h-1m-4.5 9a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
    </svg>
  );


  return (
    <div className="p-6 bg-white rounded-lg shadow-xl w-full mx-auto my-8">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-3xl font-extrabold text-gray-900">Gestión de Clientes MAP</h2>
        <button
          onClick={onAddClient}
          className="px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center space-x-2 transition-colors"
        >
          {typeof PlusCircleIcon !== 'undefined' ? <PlusCircleIcon className="h-5 w-5" /> : <DefaultPlusIcon />}
          <span>Añadir Cliente</span>
        </button>
      </div>

      {/* Sección de filtros */}
      <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center space-x-2">
          {typeof FunnelIcon !== 'undefined' ? <FunnelIcon className="h-6 w-6 text-gray-600" /> : <DefaultFunnelIcon />}
          <span>Filtros de Proyectos</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="filterOpportunityNumber" className="block text-sm font-medium text-gray-700">Nº de Oportunidad</label>
            <select
              id="filterOpportunityNumber"
              name="opportunityNumber"
              value={filters.opportunityNumber}
              onChange={handleFilterChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Todos</option>
              {opportunityNumbers.map((num) => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="filterAccountManager" className="block text-sm font-medium text-gray-700">Account Manager</label>
            <select
              id="filterAccountManager"
              name="accountManager"
              value={filters.accountManager}
              onChange={handleFilterChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Todos</option>
              {accountManagers.map((am) => (
                <option key={am} value={am}>{am}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="filterSolutionSales" className="block text-sm font-medium text-gray-700">Solution Sales</label>
            <select
              id="filterSolutionSales"
              name="solutionSales"
              value={filters.solutionSales}
              onChange={handleFilterChange}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            >
              <option value="">Todos</option>
              {solutionSales.map((ss) => (
                <option key={ss} value={ss}>{ss}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Spinner de carga para la tabla */}
      {isLoadingClients ? (
        <div className="flex justify-center items-center h-48">
          <LoadingSpinner size="lg" />
        </div>
      ) : clients.length === 0 ? (
        <p className="text-center text-gray-600 py-10">No hay clientes para mostrar con los filtros aplicados. ¡Añade uno!</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="hidden grid-cols-[1.7fr_1fr_1fr_1fr] gap-4 border-b border-gray-200 bg-gray-50 px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500 lg:grid">
            <span>Organización</span>
            <span>Oportunidad</span>
            <span>Account Manager</span>
            <span>Solution Sales</span>
          </div>
          <div className="divide-y divide-gray-200">
            {clients.map((client) => {
              const isSelected = selectedClientId === client.id;

              return (
                <div key={client.id} className="bg-white">
                  <button
                    type="button"
                    onClick={() => toggleSelectedClient(client.id)}
                    className={`block w-full px-4 py-4 text-left transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 ${isSelected ? 'bg-blue-50/70' : ''}`}
                  >
                    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1.7fr_1fr_1fr_1fr] lg:items-center lg:gap-4">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-gray-900">{client.organizationName}</p>
                        <p className="mt-1 truncate text-xs text-gray-500">{client.fantasyName || 'Sin nombre de fantasía'}</p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700">{client.opportunityNumber || 'Sin número'}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm text-gray-900">{client.accountManager}</p>
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm text-gray-700">{client.solutionSales}</p>
                      </div>
                    </div>
                  </button>
                  {isSelected && (
                    <div className="border-t border-blue-100 bg-blue-50/80 px-4 py-3">
                      <div className="flex flex-col gap-3 rounded-md border border-blue-100 bg-white px-4 py-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">Acciones para este cliente</p>
                          <p className="text-xs text-slate-500">{client.organizationName} · {client.opportunityNumber || 'Sin oportunidad'}</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => onEditClient(client)} className="inline-flex items-center gap-2 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 shadow-sm transition-colors hover:bg-indigo-50">
                            {typeof PencilIcon !== 'undefined' ? <PencilIcon className="h-4 w-4" /> : <DefaultPencilIcon />}
                            Editar
                          </button>
                          <button type="button" onClick={() => confirmDelete(client.id)} className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-700 shadow-sm transition-colors hover:bg-red-50">
                            {typeof TrashIcon !== 'undefined' ? <TrashIcon className="h-4 w-4" /> : <DefaultTrashIcon />}
                            Eliminar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Diálogo de confirmación de eliminación */}
      <CustomConfirmDialog
        show={showConfirm}
        message="¿Estás seguro de que quieres eliminar este cliente? Esta acción no se puede deshacer."
        onConfirm={handleDeleteConfirmed}
        onCancel={handleDeleteCancelled}
      />
    </div>
  );
}

export default ClientList;
